import React from 'react';
import { ResponsiveImageProps } from '../ResponsiveImage';
import { CloudinaryImageSizes, CloudinaryImageTransforms } from './types';
import generateCloudinaryUrl from './CloudinaryImageHelpers';
import { getScreenSizeBreakpoints } from '../ResponsiveImage/ResponsiveImageHelpers';
import styles from './cloudinary-image.module.scss';
import { wasServerSideRendered } from '../_internal/ssr';
import { ScreenSizeName } from '../_internal/screenSizes';

export const CLOUDFRONT_DOMAIN = 'https://d3n78nkjl8tizo.cloudfront.net';

/**
 * Default recommended transformations.
 */
const requiredTransformations: CloudinaryImageTransforms = {
  /**
   * Allows optimal image compression.
   * See Cloudinary docs: https://cloudinary.com/documentation/transformation_reference#q_quality
   */
  q: 'auto',
  /**
   * Scales image so that it takes up as much space as possible
   * within width (w) x height (h) bounding box while maintaining original aspect ratio;
   * alternatively, use `c: "fill"` to resize image to specified width and height.
   * See Cloudinary docs: https://cloudinary.com/documentation/transformation_reference#c_crop_resize
   */
  c: 'fit',
  /**
   * Returns an image in the optimal format for the requesting browser in order to minimize the file size.
   * See Cloudinary docs: https://cloudinary.com/documentation/transformation_reference#f_auto
   */
  f: 'auto',
};

/**
 * Takes the image sizes at different screen sizes and generates an img `sizes` prop.
 *
 * @example
 * sizes={ sm: 200, md: 400 }
 * returns '(min-width: 560px) 400px, 200px'
 */
const generateSizes = (sizes: CloudinaryImageSizes): string => {
  // Default: { sm: 1, md: 560, lg: 900, xl: 1140, xxl: 1730 }
  const breakpoints = getScreenSizeBreakpoints();

  // Sorted from largest to smallest, each [breakpoint, imgWidth]
  // E.g. { sm: 200, md: 400 } -> [[560, 400], [1, 200]]
  const sortedSizeEntries = Object.entries(sizes)
    .map(([screenSize, imgWidth]) => {
      const breakpoint = breakpoints[screenSize as ScreenSizeName];
      return [breakpoint, imgWidth];
    })
    .sort(([a], [b]) => b - a);

  return sortedSizeEntries
    .map(([breakpoint, imgWidth], i) => {
      if (i === sortedSizeEntries.length - 1) {
        // The smallest screen size is the base size, and doesn't need a
        // min-width media query:
        return `${imgWidth}px`;
      }

      return `(min-width: ${breakpoint}px) ${imgWidth}px`;
    })
    .join(', ');
};

const generateSrcSetBreakpoints = ({
  sizes,
  upscale,
}: {
  sizes: CloudinaryImageSizes;
  upscale: boolean;
}): number[] => {
  // Simple algorithm for now - assuming all given sizes are in fixed pixel widths,
  // we generate 1x and 2x versions of each size, and remove any redundancies.
  const widths = Object.values(sizes);
  const widths2x = upscale ? widths.map(width => width * 2) : [];

  return [...new Set([...widths, ...widths2x])].sort((a, b) => a - b);
};

/**
 * Returns an <img> srcset value, e.g.:
 * 'https://...w_200... 200w, https://...w_400... 400w'
 */
const generateSrcSet = ({
  baseUrl,
  mediaPath,
  sizes,
  transforms,
}: Pick<
  CloudinaryImageProps,
  'baseUrl' | 'mediaPath' | 'sizes' | 'transforms'
>) => {
  // Our cloudfront distribution is configured to specifically route /stitch-fix/image/(upload|fetch). If this is the
  // subpath we are using, we want to always route through cloudfront (not cloudinary), for cost reasons.
  // Other subpaths will not route correctly through cloudfront without a configuration change.
  const modifiedBaseUrl = baseUrl.startsWith(
    'https://res.cloudinary.com/stitch-fix/image',
  )
    ? baseUrl.replace('https://res.cloudinary.com', CLOUDFRONT_DOMAIN)
    : baseUrl;
  const fileExtension = mediaPath.split('.').pop();
  const isGif = fileExtension === 'gif';

  const srcWidths = generateSrcSetBreakpoints({
    sizes,
    // Cloudinary has an upper limit off 300 megapixels for animated images - upscaling gifs can make it easy to exceed that limit:
    upscale: !isGif,
  });

  const sources = srcWidths.map(width => {
    const allTransforms = {
      ...requiredTransformations,
      // fl_awebp is required for animated webp images to be displayed in browsers that support it:
      // https://cloudinary.com/documentation/transformation_reference#fl_awebp
      ...(isGif ? { fl: 'awebp' } : {}),
      ...transforms,
      w: width,
    };

    const url = generateCloudinaryUrl(
      modifiedBaseUrl,
      mediaPath,
      allTransforms,
    );

    return { url, width };
  });

  return {
    srcSet: sources.map(({ url, width }) => `${url} ${width}w`).join(', '),
    fallback: sources[0].url,
  };
};

/** This is intended to detect SSR vs SPA */
const lazyDefault = () => {
  return !wasServerSideRendered();
};

export interface CloudinaryImageProps
  extends Pick<
    ResponsiveImageProps,
    | 'alt'
    | 'lazy'
    | 'onLoad'
    | 'objectFit'
    | 'objectPosition'
    | 'fetchPriority'
    | 'width'
    | 'height'
  > {
  /**
   * The base URL for an image hosted on Cloudinary (or Cloudfront CDN).
   *
   * @example
   * Cloudinary: "https://res.cloudinary.com/stitch-fix/image/upload/"
   * Cloudfront: "https://d3n78nkjl8tizo.cloudfront.net/stitch-fix/image/upload/"
   */
  baseUrl: string;
  /**
   * A Cloudinary image's name (version and/or public id)
   *
   * @example
   * "/v1585276216/a6ttgw2xjomdl1pbzehz.jpg"
   */
  mediaPath: string;
  /**
   * Allows specifying the image's width, in pixels, at each screen size.
   * Mode-React's default screen sizes include `sm`, `md`, `lg`, `xl`, `xxl`.
   *
   * Recommended: always define an image size for the `sm` screen size.
   *
   * @example
   * ```ts
   * { sm: 200, md: 400 }
   * ```
   */
  sizes: CloudinaryImageSizes;
  /**
   * Allows applying additional image transformations across all screen sizes.
   * By default, `CloudinaryImage` applies image compression quality 'auto' and crop mode 'fit'.
   *
   * @example
   * Recommended transforms for SkuCard image:
   * ```ts
   * {
   *   e: ['trim', 'replace_color:f2f3f4:300:ffffff']
   * }
   * ```
   */
  transforms?: CloudinaryImageTransforms;
}

/**
 * Use `CloudinaryImage` to display optimized images hosted on Cloudinary.
 *
 * The `CloudinaryImage` component allows specifying the desired image's dimensions responsively, using Mode-React's screen sizes – `sm`, `md`, `lg`, `xl`, `xxl`.
 *
 * Given the `baseUrl` and `mediaPath` for an image hosted on Cloudinary (or served through the Cloudfront CDN),
 * the `CloudinaryImage` component renders a responsive image, automatically optimizing for a client's:
 *
 * - Device pixel ratio (i.e., image optimized for low and hi-res displays)
 * - Browser's supported image formats (i.e., image displayed is WebP)
 *
 * `CloudinaryImage` utilizes Cloudinary's URL based transformations ([learn more](https://cloudinary.com/documentation/transformation_reference)).
 */
const CloudinaryImage = ({
  alt,
  baseUrl,
  lazy = lazyDefault(),
  mediaPath,
  sizes,
  transforms,
  onLoad,
  objectFit = 'contain',
  objectPosition,
  fetchPriority,
  width,
  height,
}: CloudinaryImageProps): JSX.Element => {
  const { srcSet, fallback } = generateSrcSet({
    baseUrl,
    mediaPath,
    sizes,
    transforms,
  });

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      data-testid="responsive-image"
      alt={alt}
      srcSet={srcSet}
      src={fallback}
      sizes={generateSizes(sizes)}
      loading={lazy ? 'lazy' : undefined}
      onLoad={onLoad}
      width={width}
      height={height}
      // @ts-expect-error fetchpriority hasn't been added to the React types yet
      // see https://github.com/DefinitelyTyped/DefinitelyTyped/pull/63178 for more information
      // eslint-disable-next-line react/no-unknown-property
      fetchpriority={fetchPriority}
      style={{ objectPosition, objectFit }}
      className={styles.image}
    />
  );
};

export default CloudinaryImage;
